/* ---------------------------------------------------------------------------
   Bridge Rating's calls to its own deployment.

   THE SAME ORIGIN AS BRIDGE WATCH, WHICH IS THE WHOLE POINT. `bw_session` is set
   with `Path=/` and `HttpOnly`, so it rides on these requests exactly as it
   rides on Bridge Watch's — one sign-in serves both applications, and neither
   ever handles the token. See functions/lib/session.ts.

   A SEPARATE, SMALLER CLIENT THAN src/core/api.ts. That module is 900 lines of
   register, photograph, model, defect and people calls, all of them irrelevant
   here, and importing it would drag the whole of Bridge Watch's domain into this
   bundle. Four endpoints are used: health, the register, and the two rating
   routes.

   /api/health IS HOW THIS APPLICATION FINDS OUT WHAT IT MAY DO. It is the one
   unrestricted endpoint, and it reports the caller's role, the tenant, and
   whether the database is behind its code. Nothing here guesses at any of the
   three — a rating form that offered a Save button to somebody who may not save
   is worse than one that says why not.
   --------------------------------------------------------------------------- */

import type { Bridge, RegisterDefect } from '../data/types.ts';
import { readRoles, rolesLabel, type Capability, type Role } from '../data/roles.ts';
import type { RatingResult } from '../cbplr/index.ts';

/** A failed call, carrying the status so a caller can tell 403 from 503. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      /* Explicit, though same-origin is the default: this cookie is the entire
         authentication story and a future move to another host must fail loudly
         here rather than silently sign everybody out. */
      credentials: 'same-origin',
      headers: { accept: 'application/json', ...init.headers },
    });
  } catch (err) {
    /* A network failure, not a refusal. Status 0 so a caller can tell "the
       deployment said no" from "there was nobody to ask". */
    throw new ApiError(0, err instanceof Error ? err.message : 'The deployment did not answer.');
  }

  const text = await response.text();
  let body: unknown = null;
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const message =
      typeof body === 'object' && body !== null && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `${response.status} ${response.statusText}`;
    throw new ApiError(response.status, message);
  }
  return body as T;
}

/* --------------------------- health --------------------------- */

/**
 * What `/api/health` answers.
 *
 * THE ROLE AND THE CAPABILITIES ARE INSIDE `identity`, NOT BESIDE IT, and that
 * nesting is the whole shape of this type. It is not arbitrary: there is nobody
 * to have a role unless somebody is identified, so the two live where the
 * identity does and are absent together. Reading them from the top level yields
 * `undefined`, which this client turned into "no capabilities" — and every
 * control on every screen was then disabled for an engineer who had both of
 * them. `callerOf` below is the only reader, and the smoke suite checks it
 * against health.ts itself, so this cannot drift again.
 */
export interface HealthReport {
  ok: boolean;
  checks: Record<string, string>;
  tenant?: string | null;
  identity?: {
    email?: string | null;
    serviceToken?: string | null;
    author?: string | null;
    roles?: Role[] | null;
    suspended?: boolean;
    roleSource?: string | null;
    capabilities?: Capability[] | null;
  } | null;
}

/** Who the deployment says is calling, and what they may do. */
export interface Caller {
  email: string | null;
  /** The roles held, as a person reads them — "Rating engineer · Approver". Null with no identity. */
  role: string | null;
  capabilities: readonly Capability[] | null;
}

/**
 * Read the caller out of a health report.
 *
 * `null` capabilities is a real answer and is not the same as `[]`: it means
 * this deployment enforces no Access application, so there is no identity to
 * have a role. The screens say "nobody has said whether you may…" rather than
 * refusing, which is what `whyNot` in ./store.ts is for.
 */
export function callerOf(report: HealthReport): Caller {
  const identity = report.identity ?? null;
  return {
    email: identity?.email ?? null,
    role: (() => {
      const roles = readRoles(identity?.roles);
      return roles ? rolesLabel(roles, identity?.suspended === true) : null;
    })(),
    capabilities: identity?.capabilities ?? null,
  };
}

export function health(): Promise<HealthReport> {
  return call<HealthReport>('/api/health');
}

/** Whether health's schema line says this deployment is behind its migrations. */
export function schemaBehind(report: HealthReport): boolean {
  return (report.checks['schema'] ?? '').startsWith('failed');
}

/* --------------------------- the register --------------------------- */

export async function fetchRegister(): Promise<Bridge[]> {
  const body = await call<{ bridges: Bridge[] }>('/api/bridges');
  return body.bridges ?? [];
}

/* --------------------------- ratings --------------------------- */

export type RatingState = 'draft' | 'review' | 'accepted' | 'superseded';

/**
 * A rating as the deployment stores it.
 *
 * Mirrors `RatingRow` in functions/lib/ratings.ts. Declared again rather than
 * imported because that module imports Workers types the browser bundle has no
 * business seeing; the shapes are checked against each other by the smoke suite.
 */
export interface RatingRecord {
  id: string;
  bridgeId: string;
  family: string;
  continuity: string;
  position: string | null;
  spanIndex: number | null;
  profileId: string;
  profileVersion: string;
  engineVersion: string;
  exteriorDf: string | null;
  input: unknown;
  sources: Record<string, string>;
  result: RatingResult | null;
  corrections: readonly string[];
  governingRf: number | null;
  governingCheck: string | null;
  governingMethod: 'LFM' | 'ASM' | null;
  governingLevel: 'inventory' | 'operating' | null;
  postingRequired: boolean;
  state: RatingState;
  ratedBy: string | null;
  ratedAt: string;
  acceptedBy: string | null;
  acceptedAt: string | null;
  note: string | null;
  /** The model it was made against (LC-12). Absent on a demonstration rating. */
  modelId?: string | null;
  /** The revision of that model (migration 0048). */
  modelRevision?: string | null;
  /** The components it rates, off that revision's directory (migration 0049). */
  componentIds?: readonly string[];
  /** The accepted inspection its damage inputs were taken against (LC-20). */
  inspectionId?: string | null;
  /** The defects whose measurements it used. */
  defectIds?: readonly string[];
  /** Per damage field: the defect it came from, or `no-loss`. */
  damageBasis?: Record<string, string>;
  /** The rating that superseded it on acceptance (LC-22). */
  supersededBy?: string | null;
}

export async function fetchRatings(bridgeId?: string): Promise<RatingRecord[]> {
  const query = bridgeId === undefined ? '' : `?bridgeId=${encodeURIComponent(bridgeId)}`;
  const body = await call<{ ratings: RatingRecord[] }>(`/api/ratings${query}`);
  return body.ratings ?? [];
}

/**
 * The department's defects, for the damage inputs and the re-rating flag.
 *
 * The network-wide list, scoped to the caller's department on the server
 * (LC-01) — the same read Bridge Watch's overview makes.
 */
export async function fetchDefects(): Promise<RegisterDefect[]> {
  const body = await call<{ defects: RegisterDefect[] }>('/api/defects');
  return body.defects ?? [];
}

/**
 * What a structure's own cloud model can tell a rating.
 *
 * Mirrors the response of GET /api/rating/model. `readable: false` is a real
 * answer and not an error — the export is there and this deployment cannot read
 * it, which is a different situation from there being no model at all.
 */
export interface ModelFillReport {
  modelId: string;
  /** The revision of the model read (migration 0048). */
  revision?: string;
  /**
   * Whether that is the bridge's model of record (LC-12). False where none is
   * set and the newest imported model was read instead, which the form says.
   */
  modelOfRecord?: boolean;
  modelKind?: 'design' | 'as-built' | null;
  readable: boolean;
  reason?: string;
  fields?: Record<string, number>;
  sources?: Record<string, string>;
  about?: {
    model: string | null;
    bridgeType: string | null;
    exported: string | null;
    generator: string | null;
  };
  /** What the document could not answer, one sentence each. */
  notes?: string[];
  importedAt?: string | null;
}

/**
 * Read the structure's cloud model for the values a rating needs.
 *
 * Extracted server-side: the export carries thousands of triangles and a rating
 * needs about twenty numbers, so the whole document never crosses the wire.
 *
 * THE FAMILY GOES WITH THE REQUEST, because the answer depends on it — a flat
 * slab is rated in the deck's concrete and a girder in the girder's, and the
 * deck's bar mat is one family's main steel and another's transverse steel. The
 * form knows which it is, so it says.
 */
export async function fetchModelFill(
  bridgeId: string,
  family: string,
  position: string | null,
): Promise<ModelFillReport> {
  const query = new URLSearchParams({ bridgeId, family });
  if (position !== null) query.set('position', position);
  return call<ModelFillReport>(`/api/rating/model?${query.toString()}`);
}

export interface SaveRating {
  bridgeId: string;
  family: string;
  continuity: string;
  /** Which girder, for the girder families. Absent on RCFS, which the API refuses it on. */
  position?: string;
  spanIndex?: number;
  profileId?: string;
  input: unknown;
  sources: Record<string, string>;
  note?: string;
  /** The model the form was filled from (LC-12). The server stores it, or the model of record. */
  modelId?: string;
  /** The revision of it the form was filled from (migration 0048). */
  modelRevision?: string;
  /** The accepted inspection the damage inputs were filled against (LC-20). */
  inspectionId?: string;
  /** Per damage field marked `inspection`: its defect id, or `no-loss`. The server checks each. */
  damage?: Record<string, string>;
}

/**
 * Save a rating.
 *
 * SENDS THE INPUT AND NOT THE RESULT. The deployment runs the engine and returns
 * what it computed, which is what gets stored — so the number of record is the
 * deployment's, reproducible from the versions beside it rather than from
 * whatever this bundle happened to be. The form's live preview is a preview.
 */
export async function saveRating(body: SaveRating): Promise<RatingRecord> {
  const result = await call<{ rating: RatingRecord }>('/api/ratings', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return result.rating;
}

export async function setRatingState(id: string, state: RatingState): Promise<RatingRecord> {
  const result = await call<{ rating: RatingRecord }>(`/api/ratings/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ state }),
  });
  return result.rating;
}
